import { SectionLayout } from "@/components/marketing/SectionLayout";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { PageHero } from "@/components/marketing/PageHero";
import { Quote } from "@/components/marketing/Quote";
import { Btn, SectionHeader } from "@/components/marketing/primitives";
import { ContactCTA } from "@/components/marketing/ContactCTA";
import { FAQSection } from "@/components/marketing/FAQSection";
import { CASE_STUDIES_FAQS, faqJsonLd } from "@/content/faqs";
import { OG_IMAGE, abs } from "@/content/siteConfig";
import { pushAuditStart } from "@/lib/analytics";


export const Route = createFileRoute("/case-studies")({
  head: () => ({
    meta: [
      { title: "Case Studies: $5M+ Closed for Home Services Clients" },
      {
        name: "description",
        content:
          "Five accounts, start to finish: what the ads produced and what actually closed, from a landscaping job and roofing work to an eleven-year pool service account.",
      },
      { property: "og:title", content: "Case Studies: $5M+ Closed for Home Services Clients" },
      {
        property: "og:description",
        content:
          "Five accounts, start to finish: what the ads produced and what actually closed, from a landscaping job and roofing work to an eleven-year pool service account.",
      },
      { property: "og:url", content: abs("/case-studies") },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: abs("/case-studies") }],
    scripts: [{ type: "application/ld+json", children: faqJsonLd(CASE_STUDIES_FAQS) }],
  }),
  component: CaseStudiesPage,
});

const bodyStyle: React.CSSProperties = {
  fontSize: 16.5,
  lineHeight: 1.6,
  color: "rgba(0,47,167,0.78)",
  maxWidth: "62ch",
  margin: 0,
};

function Body({ children, className = "mt-5" }: { children: React.ReactNode; className?: string }) {
  return (
    <p className={className} style={bodyStyle}>
      {children}
    </p>
  );
}

function CaseMore({ children }: { children: React.ReactNode }) {
  return (
    <details className="case-more mt-6">
      <summary className="mono">Read the full case</summary>
      <div className="case-more-body">{children}</div>
    </details>
  );
}

function StatBlock({ items }: { items: string[] }) {
  return (
    <ul
      className="case-study-stats mt-12 grid grid-cols-2 gap-x-8 gap-y-3 list-none p-0 md:grid-cols-3"
    >
      {items.map((s) => (
        <li
          key={s}
          className="mono"
          style={{
            fontSize: 12,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "#002FA7",
          }}
        >
          {s}
        </li>
      ))}
    </ul>
  );
}


function CaseStudiesPage() {
  return (
    <SiteShell>
      <PageHero
        eyebrow="Case studies"
        lede={
          <>
            Most agency case studies show you a screenshot of a dashboard. Ad platforms are very
            good at producing impressive-looking numbers that never reach a bank account, so the
            results below are reported the way we report to clients: what the ads produced, and
            separately, what actually closed.
          </>
        }
        ctas={<Btn to="/audit" onClick={() => pushAuditStart("inline")}>Get your free audit →</Btn>}

      >
        Case <span className="display-italic">Studies</span>
      </PageHero>

      <section className="bhg-section">
        <SectionLayout>
          <Quote
            body="BHG audited our Google Ads before we ever paid him a dollar and showed me exactly where we were double-counting conversions. Nobody else who pitched us did that. Six months in and we finally trust our numbers."
            name="Dallas Horvath"
            role="Google review"
          />
        </SectionLayout>
      </section>

      <section className="bhg-section">
        <SectionLayout>
          <SectionHeader
            num="01"
            title={
              <>
                A single landscaping lead became a{" "}
                <span className="display-italic">$90,000 job</span>
              </>
            }
          />
          <Body className="mt-8">A landscaping and hardscaping client nearly switched off a campaign that looked expensive per lead. One lead from it closed at $90,000.</Body>
          <CaseMore>
          <Body>That is why we do not optimize toward cost per lead: an account tuned for the cheapest lead starves the campaigns that produce the valuable ones. Booked value is what counts.</Body>
          </CaseMore>
        </SectionLayout>
      </section>

      <section className="bhg-section">
        <SectionLayout>
          <SectionHeader
            num="02"
            title={
              <>
                $50,000 in closed roofing work from{" "}
                <span className="display-italic">two deals in a month</span>
              </>
            }
          />
          <Body className="mt-8">A roofing contractor booked two deals worth $50,000 combined in a single month.</Body>
          <CaseMore>
          <Body>Roofing leads arrive as calls, so if call tracking is misconfigured the ad platform records nothing. Measurement came before any bid or budget work, whether calls came from <Link to={"/services/local-services-ads" as never}>Local Services Ads for roofing contractors</Link> or paid search.</Body>
          </CaseMore>
        </SectionLayout>
      </section>

      <section className="bhg-section">
        <SectionLayout>
          <SectionHeader
            num="03"
            title={
              <>
                $33,435 in booked revenue across{" "}
                <span className="display-italic">two months for a door company</span>
              </>
            }
          />
          <Body className="mt-8">A commercial door company booked $13,435 in June and $20,000 in May: $33,435 of closed business across two months.</Body>
          <CaseMore>
          <Body>It produces around 30 leads a month at roughly $205 each on about $5,800 of monthly spend through <Link to={"/services/google-ads" as never}>Google Ads campaign management</Link>. The close rate, not the cost per lead, makes it work, so we report both.</Body>
          </CaseMore>
        </SectionLayout>
      </section>

      <section className="bhg-section">
        <SectionLayout>
          <SectionHeader
            num="04"
            title={
              <>
                85 leads a month at $9 each in a{" "}
                <span className="display-italic">recreation business</span>
              </>
            }
          />
          <Body className="mt-8">A pickleball and padel facility averages about 85 conversions a month at roughly $9 each on under $1,000 of monthly spend, with a conversion rate near 10%, mostly through <Link to={"/services/meta-ads" as never}>Meta Ads for Facebook and Instagram</Link>.</Body>
          <CaseMore>
          <Body>Not every business has a $90,000 job. Here the work is efficiency: cost per action low enough that the math works at scale.</Body>
          </CaseMore>
        </SectionLayout>
      </section>

      <section className="bhg-section">
        <SectionLayout>
          <SectionHeader
            num="05"
            title={
              <>
                Eleven years on one <span className="display-italic">pool service account</span>
              </>
            }
          />
          <Body className="mt-8">Our longest-running Google Ads account has been managed continuously since August 2015 and produces around 26 leads a month at about $115 each.</Body>
          <CaseMore>
          <Body>Every client is month-to-month with no long-term contract, so eleven years is eleven years of choosing to stay.</Body>
          </CaseMore>
        </SectionLayout>
      </section>

      <section className="bhg-section">
        <SectionLayout>
          <Quote
            body="Our previous agency didn't really understand Google Tag Manager or proper conversion tracking, so it was hard to know the real results from our campaigns. Berk Hospitality fixed this by setting up Google Tag Manager and accurate conversion tracking, which gave us a much clearer picture of performance."
            name="Waheed Ahmad"
            centered
            role="Google review"
          />
        </SectionLayout>
      </section>

      <section className="bhg-section">
        <SectionLayout>
          <SectionHeader
            num="06"
            title={
              <>
                The 236% reporting error{" "}
                <span className="display-italic">we didn't make</span>
              </>
            }
          />
          <StatBlock
            items={[
          "118 verified purchases",
          "$4.16 per purchase",
          "8 duplicate action types removed",
        ]}
          />

          <Body className="mt-8">A default setup would have reported 354 sales from 118 real purchases; we caught it before reporting anything.</Body>
          <CaseMore>
          <Body>This direct-to-consumer brand's Meta account logged the same transactions under eight action names. Counting all of them triples the truth, in the client's favor, on a dashboard nobody would question.</Body>
          <Body>We checked it against the platform's own interface and kept the one action type that maps to a real order. Verified for a 30-day window: 118 purchases at $4.16 each, 48 add-to-carts and 37 checkouts initiated.</Body>
          <Body>Reporting 354 would have looked three times better and sent inventory and budget decisions after a number that did not exist. Every account we take on gets this audit first.</Body>
          </CaseMore>
        </SectionLayout>
      </section>

      <section className="bhg-section">
        <SectionLayout>
          <SectionHeader
            num="07"
            title={
              <>
                38,693 leads at a{" "}
                <span className="display-italic">$68 lead cost</span>
              </>
            }
          />
          <StatBlock
            items={[
          "38,693 leads",
          "$68.49 cost per lead",
          "3.9% conversion rate",
        ]}
          />

          <Body className="mt-8">This insurance account produced 38,693 leads on Google Ads at a blended $68.49 per lead.</Body>
          <CaseMore>
          <Body>Insurance is among the costliest search categories. Leads split into 24,985 inbound calls and 12,867 form submissions, from 66 million impressions and 998,623 clicks at a 3.9% conversion rate.</Body>
          <Body>National carriers, where most accounts waste money, came in cheap: AAA auto insurance searches produced 1,495 leads at $22.91 and GEICO customer service 341 at $22.44, roughly a third of the blended cost.</Body>
          <Body>The closest regional rival produced 2,984 leads at $63.59. Los Angeles County alone returned 22,670 leads at $67.61, holding the account average as budget grew.</Body>
          </CaseMore>
        </SectionLayout>
      </section>

      <section className="bhg-section">
        <SectionLayout>
          <SectionHeader
            num="08"
            title={
              <>
                What these numbers are, <span className="display-italic">precisely</span>
              </>
            }
          />

          <Body className="mt-8">We keep two kinds of number apart:</Body>
          <ul
            className="case-study-definitions mt-10 w-full list-none p-0"
          >
            <li
              style={{
                padding: "18px 0",
                borderBottom: "1px solid rgba(0,47,167,0.12)",
                ...bodyStyle,
              }}
            >Leads and conversions are actions recorded by Google or Meta, such as a form submission or tracked call: what the advertising produced.</li>
            <li
              style={{
                padding: "18px 0",
                borderBottom: "1px solid rgba(0,47,167,0.12)",
                ...bodyStyle,
              }}
            >Closed and booked figures are jobs our clients told us they won, recorded in the month they closed.</li>
          </ul>
          <Body>Blurring them shows the more flattering one. Across the agency we have supported more than $5M in booked revenue for our clients.</Body>
        </SectionLayout>
      </section>

      <section className="bhg-section">
        <SectionLayout>
          <Quote
            body="Wasn't sure about hiring another agency. Berk Hospitality Group didn't oversell. Told me what they could and couldn't fix in the first 90 days, then did it. 10/10"
            name="Brett Emershad"
            role="Google review"
          />
        </SectionLayout>
      </section>

      <section className="bhg-section">
        <SectionLayout>
          <SectionHeader
            num="09"

            title={
              <>
                What we would show you about{" "}
                <span className="display-italic">your own account</span>
              </>
            }
          />
          <Body className="mt-8">First, whether your conversion tracking records what you think it does: if measurement is wrong, every number after it is wrong.</Body>
          <Body>This is common: in a scan of 9,560 home services websites as of October 2026, more than 1 in 4 businesses we confirmed were running ads had no conversion tracking at all.</Body>
          <Body><Link to={"/contact" as never}>Book a paid media audit</Link> and we will start there, or read <Link to={"/about" as never}>who runs these accounts and how the team is set up</Link> first.</Body>
        </SectionLayout>
      </section>

      <FAQSection items={CASE_STUDIES_FAQS} />

      <ContactCTA />
    </SiteShell>
  );
}
